import { Box, Stack, StackProps, Typography, TypographyProps } from '@mui/material';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';

import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import { boldWeight, minorTextGrey } from '@/theme';
import { Nullable } from '@/utils';

export type DisplayCurrencyProps = {
    value?: number;
    currency?: Nullable<string>;
    variant?: TypographyProps['variant'] | 'total';
    negativeValuesBehavior?: 'parentheses' | 'minus-sign';
    /**
     * If true, the `value` prop is interpreted as hundreths of the base `currency`
     */
    cents?: boolean;
    /**
     * If there is no fractional part of the value to display, omit the ".00"
     */
    hideZeroCents?: boolean;
    /**
     * If true, the negative of the provided `value` is displayed. This is not required if `value` is naturally negative
     * (unless in that case you want it displayed as positive).
     */
    negative?: boolean;
    /**
     * If true, when the DisplayCurrency has been given a `value`, but later `value` becomes undefined,
     * rather than reverting to --.--, let the old value stick around (deemphasized) and show a loading indicator.
     */
    stickyValueOnLoading?: boolean;
    sx?: StackProps['sx'];
};

export const DisplayCurrency = ({
    value,
    currency = 'USD',
    variant = 'inherit',
    negativeValuesBehavior = 'minus-sign',
    hideZeroCents = false,
    cents = false,
    negative = false,
    stickyValueOnLoading = false,
    sx,
}: DisplayCurrencyProps) => {
    const formattedCurrency = currency?.toUpperCase() || 'USD';
    const [lastValue, setLastValue] = useState<number>();

    const formattedValue = (() => {
        let v = value ?? lastValue;
        if (v === undefined) {
            return '--.--';
        }
        const currencyIcon = formattedCurrency === 'USD' ? '$' : '';
        if (cents) {
            v /= 100;
        }
        if (negative) {
            v = -v;
        }
        const minimumFractionDigits = hideZeroCents && v === Math.floor(v) ? 0 : 2;
        const baseValue = `${currencyIcon}${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits })}`;

        if (v >= 0) {
            return baseValue;
        }
        const [prefix, suffix] = negativeValuesBehavior === 'parentheses' ? ['(', ')'] : ['−', ''];

        return `${prefix}${baseValue}${suffix}`;
    })();

    useEffect(() => {
        if (stickyValueOnLoading && !isNil(value)) {
            setLastValue(value);
        }
        if (!stickyValueOnLoading) {
            setLastValue(undefined);
        }
    }, [stickyValueOnLoading, value]);

    const showLoading = stickyValueOnLoading && isNil(value);

    return (
        <Stack
            component="span"
            direction="row"
            spacing={0}
            alignItems="baseline"
            sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, ...sx }}
        >
            <Typography
                variant={variant === 'total' ? 'body1' : variant}
                component="span"
                color={showLoading ? minorTextGrey : undefined}
                sx={{ pr: 0.5, display: 'flex', alignItems: 'center', gap: '0.2em' }}
            >
                <Box
                    component="span"
                    sx={{ whiteSpace: 'nowrap', fontWeight: variant === 'total' ? boldWeight : undefined }}
                >
                    {formattedValue}
                </Box>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                    <LoadingIndicatorIcon
                        isLoading={showLoading}
                        color="inherit"
                        loadingSx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: minorTextGrey,
                        }}
                    />
                    <Typography
                        component="span"
                        variant="inherit"
                        sx={{ visibility: showLoading ? 'hidden' : undefined, fontWeight: 'unset' }}
                    >
                        {formattedCurrency}
                    </Typography>
                </Box>
            </Typography>
        </Stack>
    );
};
